import {
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
  Tab,
  Tabs,
} from '@heroui/react';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowLeft,
  FiBookOpen,
  FiMapPin,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { PageHeader } from '../../../../components/PageHeader';
import {
  useMemberActions,
  useWorkspaceDetail,
  useWorkspaces,
} from '../../hooks';
import type { DirectoryMember, MemberAssignment } from '../../types';
import {
  locationDepth,
  locationLabel,
  memberName,
  tierStyle,
} from '../../utils';
import { AddMemberModal } from '../AddMemberModal';
import { AssignRoleModal } from '../AssignRoleModal';
import { ConfirmLastRoleModal } from '../ConfirmLastRoleModal';
import { DirectoryTab } from '../DirectoryTab';
import { EditAssignmentsModal } from '../EditAssignmentsModal';
import { MemberTable } from '../MemberTable';
import { ViewAsPicker } from '../ViewAsPicker';

/** The tabs, in the order they are shown. The first one is the default. */
const WORKSPACE_TABS = ['directory', 'shared', 'places'];

export const WorkspaceDetail: React.FC = () => {
  const { workspaceId = '', tab } = useParams();
  const navigate = useNavigate();
  // The address is the source of truth for which tab is open, so a link to one
  // opens it and a reload stays put.
  const openTab = tab && WORKSPACE_TABS.includes(tab) ? tab : 'directory';
  // The three controls above the member table; each one re-reads the directory.
  const [searchTerm, setSearchTerm] = useState('');
  const [placeFilter, setPlaceFilter] = useState<string[]>([]);
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState('10');
  // Whose view the shared list answers for. Null is the caller's own, which for
  // an admin is the whole workspace.
  const [viewAsUserId, setViewAsUserId] = useState<string | null>(null);
  // There is no single-workspace read, and the admin list is deliberately
  // small and unpaginated, so the name comes from the list.
  const { workspaces } = useWorkspaces();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const {
    members,
    locations,
    isLoading,
    error,
    notFound,
    totalCount,
    pageNumber,
    hasNextPage,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    refetch,
  } = useWorkspaceDetail(
    workspaceId,
    { searchTerm, locationIds: placeFilter, teamRoleIds: roleFilter },
    Number(pageSize),
    viewAsUserId ?? undefined
  );
  const [isAddOpen, setAddOpen] = useState(false);
  const [assignFor, setAssignFor] = useState('');
  const [editFor, setEditFor] = useState('');
  const {
    removeRole,
    isWorking,
    error: actionError,
  } = useMemberActions(refetch);

  // Held while the service is asking whether losing their last role — and so
  // the workspace — is intended.
  const [lastRolePrompt, setLastRolePrompt] = useState<{
    userId: string;
    locationId: string;
    roleId: string;
  } | null>(null);

  // If it turns out to be their last, the service refuses until that is
  // acknowledged — which is what the prompt asks.
  const dropAssignment = async (
    member: DirectoryMember,
    assignment: MemberAssignment
  ) => {
    const outcome = await removeRole({
      workspaceId,
      userId: member.userId,
      locationId: assignment.locationId,
      roleId: assignment.role.id,
    });

    if (outcome.wouldRemoveFromWorkspace) {
      setLastRolePrompt({
        userId: member.userId,
        locationId: assignment.locationId,
        roleId: assignment.role.id,
      });
    }
  };

  const dropOnlyAssignment = async (member: DirectoryMember) => {
    const only = member.assignments[0];
    if (only) {
      await dropAssignment(member, only);
    }
  };

  const confirmLastRole = async () => {
    if (!lastRolePrompt) {
      return;
    }

    await removeRole({
      ...lastRolePrompt,
      workspaceId,
      allowRemoveFromWorkspace: true,
    });
    setLastRolePrompt(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <ErrorCard
          title="Could not load workspace"
          message={error.message}
          onRetry={refetch}
        />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="p-6">
        <ErrorCard
          title="Workspace not found"
          message="Check the link, or pick the workspace from the list."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Workspace - Choco Admin</title>
      </Helmet>
      <Link
        to="/workspaces"
        className="inline-flex items-center gap-2 text-small text-default-500 hover:text-foreground"
      >
        <FiArrowLeft /> All workspaces
      </Link>
      <PageHeader
        title={workspace?.name ?? 'Workspace'}
        description={workspaceId}
        actions={[
          {
            label: 'Add member',
            icon: <FiUserPlus />,
            color: 'primary',
            onClick: () => setAddOpen(true),
          },
        ]}
      />

      {isLoading && members.length === 0 && locations.length === 0 ? (
        <div className="flex justify-center p-12">
          <Spinner />
        </div>
      ) : (
        <Tabs
          aria-label="Workspace"
          selectedKey={openTab}
          onSelectionChange={(key) =>
            navigate(`/workspaces/${workspaceId}/${String(key)}`)
          }
          variant="underlined"
          classNames={{
            tabList:
              'gap-6 w-full relative rounded-none p-0 border-b border-divider',
            cursor: 'w-full bg-primary',
            tab: 'max-w-fit px-0 h-12',
            tabContent: 'group-data-[selected=true]:text-primary',
          }}
        >
          <Tab
            key="directory"
            title={
              <div className="flex items-center space-x-2">
                <FiBookOpen size={16} />
                <span>Directory</span>
              </div>
            }
          >
            <DirectoryTab workspaceId={workspaceId} locations={locations} />
          </Tab>
          <Tab
            key="shared"
            title={
              <div className="flex items-center space-x-2">
                <FiUsers size={16} />
                <span>Shared locations</span>
              </div>
            }
          >
            <Card>
              <CardHeader className="flex-col items-start gap-1">
                <p className="text-small text-default-500">
                  Everyone who works at the same places, with the roles they
                  hold there.
                </p>
                <ViewAsPicker
                  workspaceId={workspaceId}
                  selectedUserId={viewAsUserId}
                  onSelect={setViewAsUserId}
                />
              </CardHeader>
              <CardBody className="gap-4">
                {actionError && (
                  <div className="rounded-medium bg-danger-50 p-3 text-small text-danger">
                    {actionError}
                  </div>
                )}
                <MemberTable
                  members={members}
                  locations={locations}
                  searchTerm={searchTerm}
                  onSearchTermChange={setSearchTerm}
                  locationFilter={placeFilter}
                  onLocationFilterChange={setPlaceFilter}
                  roleFilter={roleFilter}
                  onRoleFilterChange={setRoleFilter}
                  isLoading={isLoading}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  totalCount={totalCount}
                  pageNumber={pageNumber}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onNextPage={goToNextPage}
                  onPreviousPage={goToPreviousPage}
                  onFirstPage={goToFirstPage}
                  actions={{
                    isWorking,
                    onRemoveOnlyAssignment: dropOnlyAssignment,
                    onEditAssignments: (member) => setEditFor(member.userId),
                    onRemoveAssignment: dropAssignment,
                  }}
                />
              </CardBody>
            </Card>
          </Tab>
          <Tab
            key="places"
            title={
              <div className="flex items-center space-x-2">
                <FiMapPin size={16} />
                <span>Organization</span>
              </div>
            }
          >
            <Card>
              <CardHeader>
                <p className="text-small text-default-500">
                  Where you can give someone a role.
                </p>
              </CardHeader>
              <CardBody className="gap-4">
                {locations.length === 0 ? (
                  <p className="text-small text-default-500">
                    Nothing to show yet for this distributor.
                  </p>
                ) : (
                  locations.map((location) => {
                    const depth = locationDepth(location.hierarchyType);
                    const tier = tierStyle(location.hierarchyType);

                    return (
                      <div
                        key={location.locationId}
                        className={`space-y-2 ${tier.background} rounded-medium px-3 py-2`}
                        style={{ marginLeft: `${depth * 48}px` }}
                      >
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/suppliers/${location.locationId}`}
                            className="font-medium text-default-700 hover:text-primary hover:underline"
                          >
                            {locationLabel(
                              location.locationName,
                              location.locationId
                            )}
                          </Link>
                          <span className="text-tiny text-default-500">
                            {tier.label}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {location.roles.map((role) => (
                            <Chip
                              key={role.id}
                              size="sm"
                              variant="flat"
                              className="text-default-600"
                            >
                              {role.displayName}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </CardBody>
            </Card>
          </Tab>
        </Tabs>
      )}

      <ConfirmLastRoleModal
        isOpen={lastRolePrompt !== null}
        memberName={memberName(
          members.find((m) => m.userId === lastRolePrompt?.userId)?.name ?? null
        )}
        isWorking={isWorking}
        onConfirm={confirmLastRole}
        onCancel={() => setLastRolePrompt(null)}
      />

      <EditAssignmentsModal
        isOpen={editFor !== ''}
        onClose={() => setEditFor('')}
        workspaceId={workspaceId}
        member={members.find((m) => m.userId === editFor)}
        onAddAssignment={setAssignFor}
        onChanged={refetch}
      />

      <AssignRoleModal
        isOpen={assignFor !== ''}
        onClose={() => setAssignFor('')}
        workspaceId={workspaceId}
        userId={assignFor}
        memberName={memberName(
          members.find((m) => m.userId === assignFor)?.name ?? null
        )}
        locations={locations}
        onChanged={refetch}
      />

      <AddMemberModal
        isOpen={isAddOpen}
        onClose={() => setAddOpen(false)}
        workspaceId={workspaceId}
        locations={locations}
        onChanged={refetch}
      />
    </div>
  );
};
